import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Terminal, Database, ChevronUp, ChevronDown, ChevronRight, AlertCircle, AlertTriangle } from 'lucide-react';
import { SimulationState } from '../../types';
import type { ValidationIssue } from '../../utils/validation';

interface BottomPanelProps {
  simulationState: SimulationState;
  onVariableChange?: (key: string, value: unknown) => void;
  issues?: ValidationIssue[];
  onFocusNode?: (nodeId: string) => void;
}

type TabId = 'logs' | 'variables' | 'issues';

const VariableRow: React.FC<{ name: string; value: unknown; depth?: number }> = ({ name, value, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const isObject = value !== null && typeof value === 'object';
  const isExpandable = isObject && Object.keys(value as object).length > 0;
  const maxExpandDepth = 2;

  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 group transition-colors">
        <td className="py-2 px-4 text-teal-700 dark:text-teal-400 font-bold whitespace-nowrap">
          <div className="flex items-center" style={{ paddingLeft: `${depth * 16}px` }}>
            {isExpandable && depth < maxExpandDepth ? (
              <button onClick={() => setExpanded(!expanded)} className="mr-1 text-slate-400 hover:text-slate-600 p-0.5 rounded">
                {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            ) : (
              <span className="mr-1 w-[18px] inline-block" />
            )}
            {depth > 0 && <span className="text-slate-300 mr-1 select-none text-[10px]">.</span>}
            {name}
          </div>
        </td>
        <td className="py-2 px-4 text-slate-700 dark:text-slate-300">
          {isObject ? (
            expanded ? (
              <span className="text-[10px] text-slate-400 italic">
                {Array.isArray(value) ? `Array(${value.length})` : `{${Object.keys(value as object).length} keys}`}
              </span>
            ) : (
              <pre className="text-[10px] bg-slate-100 dark:bg-slate-900 p-1 rounded max-h-20 overflow-auto border border-slate-200">
                {JSON.stringify(value, null, 2)}
              </pre>
            )
          ) : (
            String(value)
          )}
        </td>
        <td className="py-2 px-4 text-right"></td>
      </tr>
      {expanded && isExpandable && depth < maxExpandDepth && (
        Object.entries(value as object).map(([childKey, childVal]) => (
          <VariableRow key={`${name}.${childKey}`} name={childKey} value={childVal} depth={depth + 1} />
        ))
      )}
    </>
  );
};

const IssueRow: React.FC<{ issue: ValidationIssue; onFocusNode?: (id: string) => void }> = ({ issue, onFocusNode }) => {
  const Icon = issue.severity === 'error' ? AlertCircle : AlertTriangle;
  const iconColor = issue.severity === 'error' ? 'text-red-500' : 'text-amber-500';
  const rowBg = issue.severity === 'error' ? 'hover:bg-red-50 dark:hover:bg-red-950/20' : 'hover:bg-amber-50 dark:hover:bg-amber-950/20';

  const firstNode = issue.nodeIds?.[0];

  return (
    <div
      className={`flex items-start gap-2 px-4 py-2 border-b border-slate-100 dark:border-slate-800 ${rowBg} ${firstNode ? 'cursor-pointer' : ''}`}
      onClick={() => firstNode && onFocusNode?.(firstNode)}
      title={firstNode ? `Click to focus ${firstNode}` : undefined}
    >
      <Icon size={14} className={`${iconColor} mt-0.5 flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
            {issue.rule}
          </span>
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">
            {issue.severity}
          </span>
        </div>
        <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5 break-words">
          {issue.message}
        </div>
        {(issue.nodeIds?.length || issue.edgeIds?.length) ? (
          <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
            {issue.nodeIds?.length ? `nodes: ${issue.nodeIds.join(', ')}` : ''}
            {issue.nodeIds?.length && issue.edgeIds?.length ? ' · ' : ''}
            {issue.edgeIds?.length ? `edges: ${issue.edgeIds.join(', ')}` : ''}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const MIN_HEIGHT = 120;
const DEFAULT_HEIGHT = 250;
const MAX_HEIGHT_VH = 0.8;

const BottomPanel: React.FC<BottomPanelProps> = ({ simulationState, issues, onFocusNode }) => {
  // Collapsed by default
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('logs');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'error' | 'warning'>('all');
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Resize drag — track pointer while dragging. Compute height from distance
  // to window bottom so the panel grows upward as the user drags up.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const maxH = Math.floor(window.innerHeight * MAX_HEIGHT_VH);
      const next = Math.min(maxH, Math.max(MIN_HEIGHT, window.innerHeight - e.clientY));
      setHeight(next);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  const issueList = issues ?? [];
  const errorCount = useMemo(() => issueList.filter(i => i.severity === 'error').length, [issueList]);
  const warningCount = useMemo(() => issueList.filter(i => i.severity === 'warning').length, [issueList]);
  const filteredIssues = useMemo(() => {
    if (severityFilter === 'all') return issueList;
    return issueList.filter(i => i.severity === severityFilter);
  }, [issueList, severityFilter]);

  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (activeTab === 'logs' && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [simulationState.logs, activeTab, isOpen]);

  if (!isOpen) {
      return (
          <div className="h-9 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center px-4 justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors z-20" onClick={() => setIsOpen(true)}>
             <div className="flex gap-4 text-xs font-medium text-slate-500">
                <span className="flex items-center gap-2"><Terminal size={14}/> Logs</span>
                <span className="flex items-center gap-2"><Database size={14}/> Variables ({Object.keys(simulationState.variables).length})</span>
                <span className="flex items-center gap-2">
                    <AlertCircle size={14} className={errorCount > 0 ? 'text-red-500' : ''}/>
                    Issues
                    {(errorCount > 0 || warningCount > 0) && (
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${errorCount > 0 ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'}`}>
                            {errorCount > 0 ? `${errorCount} error${errorCount === 1 ? '' : 's'}` : `${warningCount} warning${warningCount === 1 ? '' : 's'}`}
                        </span>
                    )}
                </span>
             </div>
             <ChevronUp size={16} className="text-slate-400"/>
          </div>
      )
  }

  return (
    <div
      className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col shadow-inner z-20 relative"
      style={{ height: `${height}px` }}
    >
       {/* Resize handle */}
       <div
           onMouseDown={(e) => { e.preventDefault(); setIsDragging(true); }}
           className={`absolute top-0 left-0 right-0 h-1.5 -translate-y-1/2 cursor-ns-resize group z-30 ${isDragging ? '' : ''}`}
           title="Drag to resize"
       >
           <div className={`h-full w-full ${isDragging ? 'bg-teal-500/60' : 'bg-transparent group-hover:bg-teal-500/30'} transition-colors`} />
       </div>
       {/* Toolbar */}
       <div className="h-10 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-2 bg-slate-50 dark:bg-slate-900 select-none">
           <div className="flex gap-1 pt-1 h-full items-end">
               <button
                  onClick={() => setActiveTab('logs')}
                  className={`px-4 py-2 text-xs font-bold flex items-center gap-2 rounded-t-lg border-t border-x transition-all h-[calc(100%-4px)]
                    ${activeTab === 'logs'
                        ? 'text-teal-700 bg-white border-slate-200 border-b-transparent shadow-[0_-2px_4px_rgba(0,0,0,0.02)] z-10'
                        : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'}`}
                >
                   <Terminal size={14}/> Execution Logs
               </button>
               <button
                  onClick={() => setActiveTab('variables')}
                  className={`px-4 py-2 text-xs font-bold flex items-center gap-2 rounded-t-lg border-t border-x transition-all h-[calc(100%-4px)]
                    ${activeTab === 'variables'
                        ? 'text-teal-700 bg-white border-slate-200 border-b-transparent shadow-[0_-2px_4px_rgba(0,0,0,0.02)] z-10'
                        : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'}`}
                >
                   <Database size={14}/> Variables
                   <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === 'variables' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>
                       {Object.keys(simulationState.variables).length}
                   </span>
               </button>
               <button
                  onClick={() => setActiveTab('issues')}
                  className={`px-4 py-2 text-xs font-bold flex items-center gap-2 rounded-t-lg border-t border-x transition-all h-[calc(100%-4px)]
                    ${activeTab === 'issues'
                        ? 'text-teal-700 bg-white border-slate-200 border-b-transparent shadow-[0_-2px_4px_rgba(0,0,0,0.02)] z-10'
                        : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-100'}`}
                >
                   <AlertCircle size={14} className={errorCount > 0 ? 'text-red-500' : ''}/> Issues
                   {errorCount > 0 && (
                       <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">
                           {errorCount}
                       </span>
                   )}
                   {warningCount > 0 && errorCount === 0 && (
                       <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                           {warningCount}
                       </span>
                   )}
               </button>
           </div>
           <div className="flex items-center gap-2 pr-2">
               {activeTab === 'issues' && (
                   <div className="flex gap-1 text-[10px] font-semibold">
                       {(['all', 'error', 'warning'] as const).map(f => (
                           <button
                               key={f}
                               onClick={() => setSeverityFilter(f)}
                               className={`px-2 py-0.5 rounded uppercase tracking-wide ${severityFilter === f ? 'bg-slate-700 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-300'}`}
                           >
                               {f}
                           </button>
                       ))}
                   </div>
               )}
               <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-200 rounded dark:hover:bg-slate-800 transition-colors">
                   <ChevronDown size={16}/>
               </button>
           </div>
       </div>

       {/* Content */}
       <div className="flex-1 overflow-auto p-0 font-mono text-xs bg-white dark:bg-slate-950 relative">
           {activeTab === 'logs' ? (
               <div className="p-4 space-y-1.5">
                   {simulationState.logs.length === 0 && (
                       <div className="text-slate-400 italic flex items-center gap-2 opacity-50 p-4">
                           <Terminal size={14}/>
                           Ready to simulate...
                       </div>
                   )}
                   {simulationState.logs.map((log, i) => (
                       <div key={i} className="flex gap-3 hover:bg-slate-50 dark:hover:bg-slate-900 p-1 rounded -mx-1">
                           <span className="text-slate-400 select-none min-w-[70px]">[{new Date().toLocaleTimeString().split(' ')[0]}]</span>
                           <span className="text-slate-700 dark:text-slate-300 break-all">{log}</span>
                       </div>
                   ))}
                   <div ref={logsEndRef} />
               </div>
           ) : activeTab === 'variables' ? (
               <div className="w-full">
                  <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 sticky top-0 z-10 shadow-sm">
                          <tr>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 w-1/3 text-[10px] uppercase tracking-wider">Variable Name</th>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-wider">Current Value</th>
                              <th className="font-semibold py-2 px-4 border-b border-slate-200 dark:border-slate-800 w-12"></th>
                          </tr>
                      </thead>
                      <tbody>
                        {Object.entries(simulationState.variables).map(([key, value]) => (
                            <VariableRow key={key} name={key} value={value} />
                        ))}
                        {Object.keys(simulationState.variables).length === 0 && (
                            <tr>
                                <td colSpan={3} className="py-8 text-center text-slate-400 italic">
                                    No global variables defined.
                                </td>
                            </tr>
                        )}
                      </tbody>
                  </table>
               </div>
           ) : (
               // issues tab
               <div>
                   {filteredIssues.length === 0 ? (
                       <div className="py-8 text-center text-slate-400 italic text-xs">
                           {issueList.length === 0 ? 'No issues — diagram validates cleanly.' : 'No issues match the current filter.'}
                       </div>
                   ) : (
                       filteredIssues.map((issue, i) => (
                           <IssueRow key={`${issue.rule}-${i}`} issue={issue} onFocusNode={onFocusNode} />
                       ))
                   )}
               </div>
           )}
       </div>
    </div>
  );
};

export default BottomPanel;
